/**
 * Workflow & Approvals settings tab — iter48.
 *
 * Owner/Admin-only. Two panels:
 *   1. Job Level Manager — ordered list of levels (Staff → Director)
 *   2. Approval Template Manager — request-type-scoped multi-step templates
 *
 * Every template auto-appends a "Final escalation to Owner+Admin" step
 * on the backend; that appears as a locked row here for clarity.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Stack, Plus, Trash, ArrowUp, ArrowDown, Buildings, LockKey,
  Ladder, GitBranch, WarningCircle,
} from "@phosphor-icons/react";

const REQUEST_TYPES = [
  { value: "leave_request",         label: "Leave Request" },
  { value: "attendance_correction", label: "Attendance Correction Request" },
  { value: "profile_update",        label: "Profile Update Request" },
  { value: "generic",               label: "Generic Approval Request" },
];

const APPROVER_TYPES = [
  { value: "named_person",  label: "Named Person" },
  { value: "job_level",     label: "Job Level" },
  { value: "line_manager",  label: "Line Manager (Reports To)" },
  { value: "system_role",   label: "System Role" },
];

export default function WorkflowApprovalsTab() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some(r => r === "owner" || r === "admin");
  if (!isAdmin) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Only workspace Owner or Admin can view Workflow &amp; Approvals settings.
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="workflow-approvals-tab">
      <JobLevelManager />
      <ApprovalTemplateManager />
    </div>
  );
}


// ================== Job Level Manager ==================

function JobLevelManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", rank: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workflow/job-levels");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", rank: (rows.reduce((m, r) => Math.max(m, r.rank || 0), 0) + 1) });
  };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, rank: r.rank }); };
  const cancel = () => { setEditing(null); setForm({ name: "", rank: 1 }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Level name is required");
    try {
      if (editing) {
        await api.put(`/workflow/job-levels/${editing.id}`, form);
        toast.success("Job level updated");
      } else {
        await api.post("/workflow/job-levels", form);
        toast.success("Job level created");
      }
      cancel();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete job level "${r.name}"? This will fail if any employees currently hold this level.`)) return;
    try {
      const { data } = await api.delete(`/workflow/job-levels/${r.id}`);
      if (data.ok === false && data.reason === "assigned") {
        const names = (data.employees || []).map(e => `${e.employee_number || ""} ${e.name}`).join(", ");
        toast.error(`Cannot delete — ${data.count} employees still hold this level: ${names}`);
        return;
      }
      toast.success("Job level deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (r, direction) => {
    // Simple rank swap with neighbour
    const sorted = [...rows].sort((a, b) => a.rank - b.rank);
    const idx = sorted.findIndex(x => x.id === r.id);
    const other = sorted[idx + direction];
    if (!other) return;
    try {
      await api.put(`/workflow/job-levels/${r.id}`, { name: r.name, rank: other.rank });
      await api.put(`/workflow/job-levels/${other.id}`, { name: other.name, rank: r.rank });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => (a.rank || 0) - (b.rank || 0)), [rows]);

  return (
    <Card data-testid="job-level-manager">
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Ladder size={18} weight="duotone" className="text-primary" />
            Job Levels
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Define the ordered seniority hierarchy for your workplace. Higher rank means greater
            seniority. Levels are referenced by approval templates and the Employee Master.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2 border rounded-md p-3 bg-muted/20">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Level name</Label>
            <Input
              placeholder="e.g. Team Lead"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="job-level-name-input"
            />
          </div>
          <div className="w-24 space-y-1">
            <Label className="text-xs">Rank</Label>
            <Input
              type="number" min={0} max={1000}
              value={form.rank}
              onChange={e => setForm(f => ({ ...f, rank: parseInt(e.target.value || 0) }))}
              data-testid="job-level-rank-input"
            />
          </div>
          <Button onClick={save} data-testid="job-level-save-btn">
            {editing ? "Save changes" : <><Plus size={13} className="mr-1" /> Add level</>}
          </Button>
          {editing && <Button variant="outline" onClick={cancel}>Cancel</Button>}
          {!editing && <Button variant="outline" onClick={openCreate}>Reset</Button>}
        </div>

        {loading && <div className="text-sm text-muted-foreground p-4">Loading…</div>}
        {!loading && sorted.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground border rounded-md">
            No job levels configured yet. Add levels above (e.g. Staff, Senior Staff, Team Lead, Manager, Director).
          </div>
        )}
        <div className="divide-y border rounded-md">
          {sorted.map((r, i) => (
            <div key={r.id} className="flex items-center gap-2 p-2.5 hover:bg-muted/40" data-testid={`job-level-row-${r.id}`}>
              <span className="w-10 text-[11px] text-muted-foreground font-mono">#{r.rank}</span>
              <span className="flex-1 text-sm font-medium">{r.name}</span>
              <Button variant="ghost" size="sm" onClick={() => move(r, -1)} disabled={i === 0} title="Move up" data-testid={`job-level-up-${r.id}`}>
                <ArrowUp size={13} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => move(r, +1)} disabled={i === sorted.length - 1} title="Move down" data-testid={`job-level-down-${r.id}`}>
                <ArrowDown size={13} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`job-level-edit-${r.id}`}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(r)} className="text-rose-600 hover:text-rose-700" data-testid={`job-level-delete-${r.id}`}>
                <Trash size={13} />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ================== Approval Template Manager ==================

const EMPTY_STEP = {
  step_number: 1,
  approver_type: "line_manager",
  spec: {},
  sla_working_days: 2,
};

const _newStepKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`;
const _mkStep = (i = 0) => ({ ...EMPTY_STEP, step_number: i + 1, _ui_key: _newStepKey() });

function ApprovalTemplateManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replaceDup, setReplaceDup] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workflow/approval-templates");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setReplaceDup(false);
    setDuplicateInfo(null);
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setReplaceDup(false);
    setDuplicateInfo(null);
    setDialogOpen(true);
  };

  const remove = async (t) => {
    if (!window.confirm(`Deactivate template "${t.name}"? Existing in-flight requests are unaffected.`)) return;
    try {
      await api.delete(`/workflow/approval-templates/${t.id}`);
      toast.success("Template deactivated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="approval-template-manager">
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <GitBranch size={18} weight="duotone" className="text-primary" />
            Approval Templates
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Define who approves each type of request. Every template automatically appends a
            final escalation to Owner + Admin.
          </p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="template-create-btn"><Plus size={14} className="mr-1" /> New template</Button>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-muted-foreground p-4">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground border rounded-md">
            No approval templates yet. When a request is submitted with no template, it routes to Admin as a default.
          </div>
        )}
        <div className="space-y-2">
          {rows.map(t => (
            <div key={t.id} className="border rounded-md p-3 bg-muted/10 space-y-2" data-testid={`template-row-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{t.name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {(REQUEST_TYPES.find(r => r.value === t.request_type) || {}).label || t.request_type}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {t.scope === "all" ? "All departments" : `${(t.departments || []).length} department(s)`}
                </Badge>
                {t.active
                  ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Active</Badge>
                  : <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">Inactive</Badge>
                }
                <div className="ml-auto flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-rose-600 hover:text-rose-700" data-testid={`template-deactivate-${t.id}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground pl-1">
                {(t.steps || []).map((s, i) => (
                  <span key={s.step_number ?? `step-${i}`} className="mr-1">
                    <b>Step {s.step_number}</b>: {(APPROVER_TYPES.find(a => a.value === s.approver_type) || {}).label} • SLA {s.sla_working_days}d
                    {i < (t.steps || []).length - 1 ? " → " : ""}
                  </span>
                ))}
                {" → "}<span className="text-primary/80">Final: Owner + Admin (locked)</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <TemplateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        replaceDup={replaceDup}
        setReplaceDup={setReplaceDup}
        duplicateInfo={duplicateInfo}
        setDuplicateInfo={setDuplicateInfo}
        onSaved={async () => { setDialogOpen(false); await load(); }}
      />
    </Card>
  );
}


function TemplateDialog({ open, onClose, editing, onSaved, replaceDup, setReplaceDup, duplicateInfo, setDuplicateInfo }) {
  const [form, setForm] = useState({
    name: "", request_type: "leave_request", scope: "all",
    departments: [], steps: [{ ...EMPTY_STEP }], active: true,
  });
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [jobLevels, setJobLevels] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [d, j, r, e] = await Promise.all([
          api.get("/workflow/meta/departments"),
          api.get("/workflow/job-levels"),
          api.get("/workflow/meta/system-roles"),
          api.get("/workforce/employees/reports-to-options"),
        ]);
        setDepartments(d.data || []);
        setJobLevels(j.data || []);
        setSystemRoles(r.data || []);
        setEmployees(e.data || []);
      } catch (err) { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "",
        request_type: editing.request_type,
        scope: editing.scope || "all",
        departments: editing.departments || [],
        steps: editing.steps?.length
          ? editing.steps.map(s => ({ ...s, spec: s.spec || {}, _ui_key: _newStepKey() }))
          : [_mkStep(0)],
        active: editing.active !== false,
      });
    } else {
      setForm({ name: "", request_type: "leave_request", scope: "all",
                departments: [], steps: [_mkStep(0)], active: true });
    }
    setDuplicateInfo(null);
    setReplaceDup(false);
  }, [editing, open, setDuplicateInfo, setReplaceDup]);

  const updateStep = (i, key, val) => setForm(f => {
    const steps = [...f.steps];
    if (key === "approver_type") {
      steps[i] = { ...steps[i], approver_type: val, spec: {} };  // reset spec on type change
    } else if (key.startsWith("spec.")) {
      const k = key.slice(5);
      steps[i] = { ...steps[i], spec: { ...steps[i].spec, [k]: val } };
    } else {
      steps[i] = { ...steps[i], [key]: val };
    }
    return { ...f, steps };
  });
  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, _mkStep(f.steps.length)] }));
  const removeStep = (i) => setForm(f => {
    if (f.steps.length <= 1) { toast.error("Template must have at least one step."); return f; }
    return { ...f, steps: f.steps.filter((_, k) => k !== i).map((s, k) => ({ ...s, step_number: k + 1 })) };
  });
  const moveStep = (i, dir) => setForm(f => {
    const steps = [...f.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return f;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...f, steps: steps.map((s, k) => ({ ...s, step_number: k + 1 })) };
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    if (form.scope === "departments" && (form.departments || []).length === 0) {
      return toast.error("Select at least one department when scope is 'Specific departments'.");
    }
    if (!form.steps.length) return toast.error("Add at least one step");
    for (const s of form.steps) {
      if (s.approver_type === "named_person" && !s.spec?.employee_id) return toast.error("Choose the named person for each Named Person step.");
      if (s.approver_type === "job_level" && !s.spec?.level_id)         return toast.error("Choose the job level for each Job Level step.");
      if (s.approver_type === "system_role" && !s.spec?.role_key)       return toast.error("Choose the system role for each System Role step.");
    }
    setSaving(true);
    try {
      const url = editing
        ? `/workflow/approval-templates/${editing.id}${replaceDup ? "?replace=true" : ""}`
        : `/workflow/approval-templates${replaceDup ? "?replace=true" : ""}`;
      const method = editing ? "put" : "post";
      // Strip client-only step keys before POST so the backend never
      // sees `_ui_key` (added purely for stable React reconciliation).
      const payload = {
        ...form,
        steps: (form.steps || []).map(({ _ui_key, ...rest }) => rest),
      };
      await api[method](url, payload);
      toast.success(editing ? "Template updated" : "Template created");
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "duplicate_template") {
        setDuplicateInfo(detail);
        toast.error(detail.message || "Duplicate template");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit template" : "New approval template"}</DialogTitle>
          <DialogDescription className="text-xs">
            Every template auto-appends a final escalation to Owner + Admin. That step cannot be removed.
          </DialogDescription>
        </DialogHeader>

        {duplicateInfo && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="template-duplicate-warning">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <WarningCircle size={16} className="text-amber-700" />
              <b>Duplicate template</b>: an active template already handles this request type &amp; scope
              (<i>{duplicateInfo.existing_name}</i>).
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setReplaceDup(true)} disabled={replaceDup} data-testid="replace-dup-btn">
                {replaceDup ? "Will replace on save" : "Replace existing"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setDuplicateInfo(null); setReplaceDup(false); }}>Cancel replacement</Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Template name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="template-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Request type *</Label>
              <Select value={form.request_type} onValueChange={v => setForm(f => ({ ...f, request_type: v }))}>
                <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map(r => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scope</Label>
              <Select value={form.scope} onValueChange={v => setForm(f => ({ ...f, scope: v }))}>
                <SelectTrigger data-testid="template-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  <SelectItem value="departments">Specific departments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="template-active-switch" />
              <span className="text-sm">Active</span>
            </div>
          </div>

          {form.scope === "departments" && (
            <div className="space-y-1">
              <Label className="text-xs">Departments this template applies to</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded bg-muted/20 max-h-40 overflow-y-auto">
                {departments.length === 0 && (
                  <div className="text-[11px] text-muted-foreground p-1">
                    No departments found on any employee yet.
                  </div>
                )}
                {departments.map(d => {
                  const on = (form.departments || []).includes(d);
                  return (
                    <label key={d} className={`text-xs px-2 py-1 rounded border cursor-pointer ${on ? "bg-primary/10 border-primary text-primary" : "bg-white"}`}>
                      <Checkbox
                        className="mr-1.5 inline-block align-middle"
                        checked={on}
                        onCheckedChange={v => setForm(f => ({
                          ...f,
                          departments: v ? [...(f.departments || []), d] : (f.departments || []).filter(x => x !== d),
                        }))}
                        data-testid={`dept-check-${d}`}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Approval steps</Label>
              <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn"><Plus size={13} className="mr-1" /> Add step</Button>
            </div>
            {form.steps.map((s, i) => (
              <div key={s._ui_key || `step-${s.step_number}`} className="border rounded p-3 space-y-2 bg-muted/10" data-testid={`step-row-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono bg-primary/10 text-primary rounded px-1.5 py-0.5">Step {s.step_number}</span>
                  <Button variant="ghost" size="sm" onClick={() => moveStep(i, -1)} disabled={i === 0}><ArrowUp size={12} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => moveStep(i, +1)} disabled={i === form.steps.length - 1}><ArrowDown size={12} /></Button>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">SLA</span>
                    <Input
                      type="number" min={1} max={30}
                      className="w-16 h-7 text-xs"
                      value={s.sla_working_days}
                      onChange={e => updateStep(i, "sla_working_days", parseInt(e.target.value || 2))}
                      data-testid={`step-sla-${i}`}
                    />
                    <span className="text-[10px] text-muted-foreground">working days</span>
                    <Button variant="ghost" size="sm" onClick={() => removeStep(i)} className="text-rose-600 hover:text-rose-700"><Trash size={13} /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Approver type</Label>
                    <Select value={s.approver_type} onValueChange={v => updateStep(i, "approver_type", v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`step-atype-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{APPROVER_TYPES.map(a => (<SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">
                      {s.approver_type === "line_manager" ? "Requester's Reports To will be used" : "Choose approver"}
                    </Label>
                    {s.approver_type === "named_person" && (
                      <Select value={s.spec?.employee_id || ""} onValueChange={v => updateStep(i, "spec.employee_id", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`step-emp-${i}`}><SelectValue placeholder="Choose employee…" /></SelectTrigger>
                        <SelectContent>
                          {employees.map(e => (
                            <SelectItem key={e.id} value={e.id}>
                              {(e.employee_number || "").padEnd(10)} — {e.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {s.approver_type === "job_level" && (
                      <Select value={s.spec?.level_id || ""} onValueChange={v => updateStep(i, "spec.level_id", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`step-level-${i}`}><SelectValue placeholder="Choose level…" /></SelectTrigger>
                        <SelectContent>{jobLevels.map(l => (<SelectItem key={l.id} value={l.id}>{l.name} (rank {l.rank})</SelectItem>))}</SelectContent>
                      </Select>
                    )}
                    {s.approver_type === "system_role" && (
                      <Select value={s.spec?.role_key || ""} onValueChange={v => updateStep(i, "spec.role_key", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`step-role-${i}`}><SelectValue placeholder="Choose role…" /></SelectTrigger>
                        <SelectContent>{systemRoles.map(r => (<SelectItem key={r.key} value={r.key}>{r.name || r.key}</SelectItem>))}</SelectContent>
                      </Select>
                    )}
                    {s.approver_type === "line_manager" && (
                      <div className="text-[11px] text-muted-foreground p-2 border rounded bg-white">
                        <b>Auto-resolved</b> to each requester&apos;s Reports To employee.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className="border-2 border-dashed border-muted rounded p-3 bg-muted/20 flex items-center gap-2 text-xs text-muted-foreground">
              <LockKey size={13} /> <b>Final escalation</b>: Owner + Admin (locked, cannot be removed)
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="template-save-btn">
            {saving ? "Saving…" : (editing ? "Save changes" : (replaceDup ? "Replace and create" : "Create template"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
